import Fund from '@/models/Fund';
import { AnalysisService } from '@/modules/analysis/services';
import { IAnalysisState } from '@/modules/analysis/store/state';
import {
  FundAnalysis,
  FundAnalysisParams,
  Paginated,
  PaginatedV2,
  SubFundAnalysisFilters,
  SubfundAnalysis,
  SchemeAnalysis,
  SchemeAnalysisFilters,
  SubmissionStatusMapping,
  SubmissionSummary,
  ConsultantAnalysisFilters,
  OrganisationAnalysis,
  ProviderAnalysisFilters,
  FiduciaryAnalysisFilters,
  FiduciaryAnalysis
} from '@/modules/analysis/types';
import { IRootState } from '@/store/state';
import { ActionContext, ActionTree } from 'vuex';
import * as AT from './actionTypes';
import * as MT from './mutationsTypes';
import { StubIGGFundAnalysisData } from '@/modules/analysis/store/stub-data';

/**
 * --------------------------------------------------
 *  Analysis Actions
 * --------------------------------------------------
 **/
export const AnalysisActions: IAnalysisActions = {
  [AT.FETCH_FUND_ANALYSIS_V2]: async (
    { commit },
    params?: FundAnalysisParams
  ): Promise<Paginated<FundAnalysis>> => {
    commit(MT.SET_FUND_ANALYSIS, null);
    if (params?.useStubData) {
      commit(MT.SET_FUND_ANALYSIS, StubIGGFundAnalysisData);
      return StubIGGFundAnalysisData;
    }
    return AnalysisService.GET_FUND_ANALYSIS_V2(params).then(
      (data: Paginated<FundAnalysis>) => {
        commit(MT.SET_FUND_ANALYSIS, data);
        return data;
      }
    );
  },

  [AT.FETCH_FUND_ANALYSIS_V2_FROM_ORG_FUNDS]: async (
    { getters, dispatch },
    params: {
      clientId: string;
      useExposedFunds: boolean;
      page?: number;
      limit?: number;
    }
  ): Promise<Paginated<FundAnalysis>> => {
    const organizationFunds: Fund[] = params?.useExposedFunds
      ? getters?.organizationExposedFunds
      : getters?.organizationFunds;
    const fundInvestmentIds = organizationFunds?.map(f => f?.id);
    const filterParams: FundAnalysisParams = {
      filters: {
        clientId: params.clientId,
        fundIds: fundInvestmentIds,
        page: params?.page ?? 1,
        limit: params?.limit ?? fundInvestmentIds?.length,
        status: SubmissionStatusMapping.EXPOSED
      }
    };
    return dispatch(AT.FETCH_FUND_ANALYSIS_V2, filterParams);
  },

  [AT.FETCH_SCHEME_ANALYSIS]: async (
    { commit },
    params?: SchemeAnalysisFilters
  ): Promise<PaginatedV2<SchemeAnalysis>> => {
    commit(MT.SET_SCHEME_ANALYSIS, null);
    return AnalysisService.GET_SCHEME_ANALYSIS(params).then(
      (data: PaginatedV2<SchemeAnalysis>) => {
        commit(MT.SET_SCHEME_ANALYSIS, data);
        return data;
      }
    );
  },

  [AT.FETCH_SUBMISSION_SUMMARY]: async (
    { commit },
    params: {
      clientId: string;
      submissionId: string;
    }
  ): Promise<SubmissionSummary> => {
    commit(MT.SET_SUBMISSION_SUMMARY, null);
    return AnalysisService.GET_SUBMISSION_SUMMARY(params).then(
      (data: SubmissionSummary) => {
        commit(MT.SET_SUBMISSION_SUMMARY, data);
        return data;
      }
    );
  },

  [AT.FETCH_SUBFUND_ANALYSIS]: async (
    { commit },
    params?: SubFundAnalysisFilters
  ): Promise<PaginatedV2<SubfundAnalysis>> => {
    return AnalysisService.GET_SUBFUND_ANALYSIS(params).then(
      (data: PaginatedV2<SubfundAnalysis>) => {
        commit(MT.SET_SUBFUND_ANALYSIS, data);
        return data;
      }
    );
  },

  [AT.FETCH_CONSULTANT_ANALYSIS]: async (
    { commit },
    params: ConsultantAnalysisFilters
  ): Promise<OrganisationAnalysis> => {
    return AnalysisService.GET_CONSULTANT_ANALYSIS(params).then(
      (data: OrganisationAnalysis) => {
        commit(MT.SET_CONSULTANT_ANALYSIS, data);
        return data;
      }
    );
  },

  [AT.FETCH_PROVIDER_ANALYSIS]: async (
    { commit },
    params: ProviderAnalysisFilters
  ): Promise<OrganisationAnalysis> => {
    return AnalysisService.GET_PROVIDER_ANALYSIS(params).then(
      (data: OrganisationAnalysis) => {
        commit(MT.SET_PROVIDER_ANALYSIS, data);
        return data;
      }
    );
  },

  [AT.FETCH_FIDUCIARY_ANALYSIS]: async (
    { commit },
    params: FiduciaryAnalysisFilters
  ): Promise<PaginatedV2<FiduciaryAnalysis>> => {
    return AnalysisService.GET_FIDUCIARY_ANALYSIS(params).then(
      (data: PaginatedV2<FiduciaryAnalysis>) => {
        commit(MT.SET_FIDUCIARY_ANALYSIS, data);
        return data;
      }
    );
  }
};
/**
 * --------------------------------------------------
 * Analysis Action Context Interface
 * --------------------------------------------------
 **/
export interface IAnalysisActionContext
  extends ActionContext<IAnalysisState, IRootState> {}

/**
 * --------------------------------------------------
 * Analysis Actions Interface
 * --------------------------------------------------
 **/
export interface IAnalysisActions
  extends ActionTree<IAnalysisState, IRootState> {
  [AT.FETCH_FUND_ANALYSIS_V2](
    context: IAnalysisActionContext,
    params?: FundAnalysisParams
  ): Promise<Paginated<FundAnalysis>>;

  [AT.FETCH_FUND_ANALYSIS_V2_FROM_ORG_FUNDS](
    context: IAnalysisActionContext,
    params: {
      clientId: string;
      useExposedFunds: boolean;
      page?: number;
      limit?: number;
    }
  ): Promise<Paginated<FundAnalysis>>;

  [AT.FETCH_SCHEME_ANALYSIS](
    context: IAnalysisActionContext,
    params?: SchemeAnalysisFilters
  ): Promise<PaginatedV2<SchemeAnalysis>>;

  [AT.FETCH_SUBMISSION_SUMMARY](
    context: IAnalysisActionContext,
    params: {
      clientId: string;
      submissionId: string;
    }
  ): Promise<SubmissionSummary>;

  [AT.FETCH_SUBFUND_ANALYSIS](
    context: IAnalysisActionContext,
    params?: SubFundAnalysisFilters
  ): Promise<PaginatedV2<SubfundAnalysis>>;

  [AT.FETCH_CONSULTANT_ANALYSIS](
    context: IAnalysisActionContext,
    params?: ConsultantAnalysisFilters
  ): Promise<OrganisationAnalysis>;

  [AT.FETCH_PROVIDER_ANALYSIS](
    context: IAnalysisActionContext,
    params?: ProviderAnalysisFilters
  ): Promise<OrganisationAnalysis>;

  [AT.FETCH_FIDUCIARY_ANALYSIS](
    context: IAnalysisActionContext,
    params?: FiduciaryAnalysisFilters
  ): Promise<PaginatedV2<FiduciaryAnalysis>>;
}
